import { PolymorphicButtonBase, type PolymorphicButtonBaseProps } from '@plato/ui/primitives/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import { cn } from '@plato/ui/util/classes';
import type { ComponentPropsWithRef, ReactElement } from 'react';
import { isMobile } from 'react-device-detect';

export type MessageActionsProps = ComponentPropsWithRef<'div'>;

export function MessageActions({ className, ...props }: MessageActionsProps): ReactElement {
    return (
        <div
            data-slot="message-actions"
            className={cn('flex items-center gap-tight', className)}
            {...props}
        />
    );
}

export type MessageActionButtonProps = PolymorphicButtonBaseProps & {
    /**
     * Names the action for assistive technology, since no text is visible.
     * On hover devices it also shows as an instant tooltip under the glyph.
     */
    label: string;
    active?: boolean;
    /** Rating buttons: the active state colors the glyph by verdict instead of the default brand wash. */
    verdict?: 'positive' | 'negative';
};

export function MessageActionButton({
    active,
    className,
    label,
    verdict,
    ...props
}: MessageActionButtonProps): ReactElement {
    const button = (
        <PolymorphicButtonBase
            data-slot="message-action-button"
            data-state={active === true ? 'active' : 'inactive'}
            aria-label={label}
            aria-pressed={active}
            className={cn(
                'inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-default',
                '[@media(hover:none)]:size-9',
                'transition duration-state ease-state active:translate-px',
                'state-disabled:pointer-events-none state-disabled:opacity-disabled',
                'text-content-weak hover:bg-content/(--opacity-wash) hover:text-content',
                verdict === undefined && [
                    'data-[state=active]:bg-brand/(--opacity-wash) data-[state=active]:text-brand',
                    'hover:data-[state=active]:bg-brand/(--opacity-wash-hover)',
                ],
                verdict === 'positive' &&
                    'data-[state=active]:text-success hover:data-[state=active]:text-success',
                verdict === 'negative' &&
                    'data-[state=active]:text-danger hover:data-[state=active]:text-danger',
                "*:[svg:not([class*='size-'])]:size-3.5",
                className,
            )}
            {...props}
        />
    );

    // Nothing hovers on touch, and a tap already fires the action.
    if (isMobile) {
        return button;
    }

    // Instant: this is the icon's name, not a delayed explainer.
    return (
        <Tooltip>
            <TooltipTrigger delay={0} render={button} />
            <TooltipContent side="bottom">{label}</TooltipContent>
        </Tooltip>
    );
}
